// noinspection JSPotentiallyInvalidUsageOfThis

/*
Copyright © 2021 EPAM Systems, Inc. All Rights Reserved.
All information contained herein is, and remains the property of EPAM Systems, Inc.
and/or its suppliers and is protected by international intellectual property law.
Dissemination of this information or reproduction of this material is strictly
forbidden, unless prior written permission is obtained from EPAM Systems, Inc
*/
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
    deepEqual, getErrorMessage, getPhoneNumberForForm, getSimpleDataForm,
} from '../utils';
import { updateFormWithFormNameData as _updateFormWithFormNameData } from '../features/bookingForm/bookingFormSlice';
import { getFormData } from '../features/bookingForm/selectors';

const form = require('b_')
    .with('form');

export const formHocWithFormName = (View, { mapStateToProps } = {}) => {
    class Form extends Component {
        state = {
            isLoading: true,
            shouldUpdate: false,
        };

        componentDidMount() {
            const payload = this.stateFromProps();
            this.updateStoreData(payload);
            this.setState(state => ({
                ...state,
                isLoading: false,
            }));
        }

        // eslint-disable-next-line max-statements
        shouldComponentUpdate(nextProps) {
            const {
                fields: nextFields,
                initData: nextData,
                errors: nextErrors,
                formData: nextFormData,
            } = nextProps;
            const {
                props,
                props: {
                    fields,
                    initData,
                    formData,
                    formName,
                    errors,
                },
            } = this;

            const isFieldsNotEqual = () => !deepEqual(nextFields, fields);
            const isDataNotEqual = () => !deepEqual(nextData, initData);
            const isErrorsNotEqual = () => !deepEqual(nextErrors, errors);
            const isPropsNotEqual = () => !deepEqual(nextProps, props);

            if (isFieldsNotEqual()) {
                const newData = getSimpleDataForm(nextFormData[formName].fields);
                const payload = this.stateFromProps(newData, nextFields);
                this.updateStoreData(payload);
                return true;
            }
            if (isDataNotEqual()) {
                const payload = this.stateFromProps(nextData);
                this.updateStoreData(payload);
                return true;
            }
            if (isErrorsNotEqual()) {
                const newData = getSimpleDataForm(formData[formName].fields);
                const payload = this.stateFromProps(newData, nextFields, nextErrors);
                this.updateStoreData(payload);
                return true;
            }

            if (!formData[formName]) {
                return true;
            }

            return isPropsNotEqual();
        }

        handleChanges = key => (newValue) => {
            const {
                formData,
                onChangeData,
                formName,
                errors,
                resetErrors,
            } = this.props;
            if (typeof resetErrors === 'function' && errors) {
                resetErrors(key);
            }

            const props = {};
            const fieldType = formData[formName]?.fields[key]?.type;
            if (fieldType === 'radio' || fieldType === 'date') {
                const errorMsg = getErrorMessage({
                    ...formData[formName].fields[key],
                    value: newValue,
                });
                props.error = errorMsg;
                props.isValid = !errorMsg;
            }

            const newFields = {
                ...formData[formName].fields,
                [key]: {
                    ...formData[formName].fields[key],
                    value: newValue,
                    ...props,
                },
            };

            this.updateStoreData({ fields: newFields });
            if (typeof onChangeData === 'function') {
                onChangeData(key, newValue);
            }
        };

        makeValidation = key => (value) => {
            const {
                formData,
                formName,
                errors,
                resetErrors,
            } = this.props;
            const newField = formData[formName].fields[key];
            let errorMsg = getErrorMessage({
                ...newField,
                value,
            });
            if (typeof resetErrors === 'function' && errors) {
                resetErrors(key);
            } else {
                errorMsg = errorMsg || (errors && errors[key]);
            }
            const newFields = {
                ...formData[formName].fields,
                [key]: {
                    ...formData[formName].fields[key],
                    error: errorMsg,
                    isValid: !errorMsg,
                },
            };

            this.updateStoreData({ fields: newFields });
        };

        setInnerError = key => (error) => {
            const {
                formData,
                formName,
            } = this.props;
            const newFields = {
                ...formData[formName].fields,
                [key]: {
                    ...formData[formName].fields[key],
                    error,
                    isValid: false,
                },
            };

            this.updateStoreData({ fields: newFields });
        };

        stateFromProps = (nextData, nextFields, nextErrors) => {
            let {
                fields,
                initData = {},
                errors,
            } = this.props;
            initData = nextData || initData;
            fields = nextFields || fields;
            errors = nextErrors || errors;
            const fieldsKeys = Object.keys(fields);
            // TODO: make this function univrse and isolate from component
            // eslint-disable-next-line max-statements
            const setValue = (name) => {
                if (fields[name].type === 'switch') {
                    if (!initData[name]) {
                        return false;
                    }
                    if (initData[name].toString().length > 1) {
                        return initData[name];
                    }
                    if (fields[name].defaultValue.toString().length > 1) {
                        return fields[name].defaultValue;
                    }
                }
                if (fields[name].type === 'checkbox') {
                    return initData[name];
                }
                if (fields[name].type === 'numberInput') {
                    return initData[name];
                }
                if (fields[name].type === 'selector') {
                    return initData[name];
                }
                if (fields[name].type === 'phone') {
                    return getPhoneNumberForForm(initData[name]);
                }
                if (fields[name].type === 'currency' && !initData[name]) {
                    return 0;
                }
                if (fields[name].type === 'date' && !initData[name]) {
                    return null;
                }
                if (fields[name].type === 'img' && !initData[name]) {
                    return null;
                }
                if (fields[name].type === 'serviceCombobox' && !initData[name]) {
                    return null;
                }
                return initData[name] || fields[name].defaultValue || '';
            };

            return fieldsKeys.reduce(
                (acc, name) => {
                    const value = setValue(name);
                    const errorMsg = getErrorMessage({
                        ...fields[name],
                        value,
                    }) || (errors && errors[name]);
                    return {
                        fields: {
                            ...acc.fields,
                            [name]: {
                                ...fields[name],
                                value,
                                isValid: !errorMsg,
                                error: errors && errors[name],
                                onChange: this.handleChanges(name),
                                onBlur: this.makeValidation(name),
                                setInnerError: this.setInnerError(name),
                            },
                        },
                    };
                },
                { fields: {} },
            );
        };

        updateStoreData = (payload) => {
            const { formName } = this.props;
            this.props.updateFormWithFormNameData({
                formName,
                data: payload,
            });
        };

        resetDataToInit = () => {
            const {
                initData,
                formName,
                formData,
                onChangeData,
            } = this.props;
            const { fields } = formData[formName];

            const payload = this.stateFromProps(initData);

            this.updateStoreData(payload);
            if (onChangeData) {
                Object.keys(fields)
                    .forEach((key) => {
                        if (initData[key] !== fields[key].value) {
                            onChangeData(key, initData[key]);
                        }
                    });
            }
        };

        render() {
            const {
                state,
                props,
            } = this;
            return state.isLoading ? null : (
                <>
                    <View {...props} {...state} />
                    {props.error && <div className={form('message', { error: true })}>{props.error}</div>}
                </>
            );
        }
    }

    return connect(
        (stor) => {
            let data = getFormData(stor);
            mapStateToProps &&
            Object.keys(mapStateToProps)
                .forEach((key) => {
                    data = {
                        ...data,
                        [key]: mapStateToProps[key](stor),
                    };
                });
            return data;
        },
        dispatch => ({ updateFormWithFormNameData: props => dispatch(_updateFormWithFormNameData(props)) }),
        null,
        { forwardRef: true },
    )(Form);
};
